import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, catchError, finalize, of, take } from 'rxjs';
import { AdminRestService } from '../services/rest/admin-rest.service';
import { UserService } from '../services/user.service';
import { TranslocoService } from '@jsverse/transloco';
import { ResponseItem } from '@models/response-item.model';
import { TemplateConstants } from '../constants/template-constants';
import { ToastServiceManager } from '../services/toast-service-manager.service';
import { AddAgencyModalComponent } from './add-agency-component/add-agency-modal.component';
import { LoadingDialogComponent } from '../shared/loading-dialog/loading-dialog.component';


@Component({
  selector: 'app-data-sharing',
  templateUrl: './data-sharing.component.html',
  styleUrl: './data-sharing.component.scss',
  standalone: false,
})
export class DataSharingComponent implements OnInit, OnDestroy {
  sharingToastText!: string;

  ngOnInit(): void {
    this.loadTranslations();
    this.getSharedData();
  }

  ngOnDestroy(): void {
    this.userServiceSubscription.unsubscribe();
  }


  public infoCalloutType = TemplateConstants.InfoCalloutType;
  public errorCalloutType = TemplateConstants.ErrorCalloutType;

  isAuthorized: boolean = true;
  sharedData: Array<string> = [];
  isBusy: boolean = false;
  public etag?: string;
  public isSuperAdmin: boolean = false;
  public serviceType: string = 'Address';
  public serviceName: string = 'Geocoding';

  constructor(private readonly adminApi: AdminRestService,
    private readonly cdr: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly dialogService: DialogService,
    private readonly toastServiceManager: ToastServiceManager,
    private readonly userService: UserService,
    private readonly transloco: TranslocoService,
  ) { }

  private loadTranslations(): void {
    this.transloco.selectTranslate('DataSharing.toast.success').pipe(take(1)).subscribe(res => {
      this.sharingToastText = res;
    });
  }

  private readonly userServiceSubscription: Subscription = this.userService.UserInformation.subscribe(userInfo => {
    if (!userInfo) {
      return;
    }
    this.isSuperAdmin = userInfo.isSuperAdmin;
  });

  public getSharedData(): void {
    this.isBusy = true;
    this.adminApi.getSharingCustomers()
      .pipe(take(1),
        finalize(() => { this.isBusy = false; this.cdr.detectChanges(); }),
        catchError(err => {
          this.isAuthorized = false;
          const overridenResponse: ResponseItem<Array<string>> = {
            Item: undefined,
            ETag: undefined,
          }
          return of(overridenResponse);
        }))
      .subscribe((data) => {
        this.sharedData = data.Item!;
        this.etag = data.ETag;
      });
  }

  public onRemoveCustomerClicked(index: number): void {
    if (index < 0 || index >= this.sharedData.length) {
      return;
    }
    const customerName = this.sharedData[index];
    this.confirmationService.confirm({
      header: this.transloco.translate('DataSharing.modal.delete.header'),
      message: this.transloco.translate('DataSharing.modal.delete.content', { customerId: customerName }),     
      acceptLabel: this.transloco.translate('DataSharing.buttons.delete'),
      rejectLabel: this.transloco.translate('Buttons.cancel'),
      accept: () => {
        this.isBusy = true;
        const customers = [...this.sharedData];
        customers.splice(index, 1);
        const loadingRef: DynamicDialogRef = this.dialogService.open(LoadingDialogComponent, {
          closable: false,
          modal: true,
          dismissableMask: false,
          closeOnEscape: false,
          showHeader: false,
          data: { message: this.transloco.translate('DataSharing.text.loading') },
        });
        this.adminApi.updateSharingCustomers(customers, this.etag!)
          .pipe(take(1),
            finalize(() => {
              this.isBusy = false;
              loadingRef.close();
              this.getSharedData();
            }))
          .subscribe(() => {
            this.toastServiceManager.success(this.sharingToastText, undefined, {
              closeButton: true,
            });
          });
      },
    });
  }

  public onAddCustomerClicked(): void {
    if (!this.etag) {
      return;
    }
    const ref = this.dialogService.open(AddAgencyModalComponent, {
      header: this.transloco.translate('DataSharing.modal.add.header'),
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      width: '600px',
      data: { serviceName: this.serviceName },
    });
    ref.onClose.pipe(take(1)).subscribe((result: string[] | null) => {
      if (!result || result.length === 0) {
        return;
      }
      const merged = [...this.sharedData];
      result.forEach(c => { if (!merged.includes(c)) merged.push(c); });
      this.isBusy = true;
      const loadingRef: DynamicDialogRef = this.dialogService.open(LoadingDialogComponent, {
        closable: false,
        modal: true,
        dismissableMask: false,
        closeOnEscape: false,
        showHeader: false,
        data: { message: this.transloco.translate('DataSharing.text.loading') },
      });
      this.adminApi.updateSharingCustomers(merged, this.etag!)
        .pipe(take(1), finalize(() => { this.isBusy = false; loadingRef.close(); this.getSharedData(); }))
        .subscribe(() => {
          this.toastServiceManager.success(this.sharingToastText, undefined, { closeButton: true });
        });
    });
  }
}

